import { ReactElement } from 'react';
import { IComponentLocationServiceOfferingsFields } from 'types/contentful';
import Link from './Link';

export default function LocationOffering({
  services,
  secondRowServices,
  heading,
  secondRowHeading,
}: IComponentLocationServiceOfferingsFields): ReactElement {
  const primaryServices = [...services].sort((a, b) =>
    a.fields.service.localeCompare(b.fields.service)
  );
  const secondaryServices = [...(secondRowServices || [])]
    .slice(0, 14)
    .sort((a, b) => a.fields.service.localeCompare(b.fields.service));

  return (
    <div className="ecp-my px-site-component text-primary-actual pb-10 md:pb-20">
      {primaryServices.length > 0 ? (
        <ServiceSection heading={heading} services={primaryServices} />
      ) : null}
      {secondaryServices.length > 0 ? (
        <div className="mt-10 lg:mt-12">
          <ServiceSection heading={secondRowHeading || ''} services={secondaryServices} />
        </div>
      ) : null}
    </div>
  );
}

function ServiceSection({
  heading,
  services,
}: {
  heading: string;
  services: IComponentLocationServiceOfferingsFields['services'];
}): ReactElement {
  return (
    <div>
      <div className="flex items-center text-left">
        <h5 className="bordered-heading bh-location-heading-item font-light text-new-34 lg:text-4xl lg:leading-new-43">
          {heading}
        </h5>
      </div>

      <ul className="mt-8 grid list-none grid-cols-1 gap-4 md:mt-10 md:grid-cols-2 xl:grid-cols-3">
        {services.map((service, index) => (
          <li key={`${service.fields.service}-${index}`}>
            <ServiceCard service={service.fields.service} slug={service.fields.slug} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function ServiceCard({ service, slug }: { service: string; slug?: string }): ReactElement {
  const content = (
    <div className="flex min-h-[56px] items-center justify-between gap-4 bg-neutral px-3 py-2 md:px-4">
      <div className="flex min-w-0 items-center gap-3">
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-secondary">
          <EyeIcon />
        </span>
        <span className="truncate text-base md:text-lg text-primary-actual">{service}</span>
      </div>
      <span className="flex-shrink-0 text-secondary-actual">
        <ChevronRightIcon />
      </span>
    </div>
  );

  if (slug) {
    return (
      <Link slug={slug}>
        <a className="block">{content}</a>
      </Link>
    );
  }

  return content;
}

function EyeIcon(): ReactElement {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronRightIcon(): ReactElement {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="9"
      height="15"
      viewBox="0 0 9 15"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M1.33301 1.33301L7.33301 7.33301L1.33301 13.333"
        stroke="currentColor"
        strokeWidth="2.66667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
